import type { Trip } from "@/api.types.v2";
import { Check, MapPin, User, Zap } from "lucide-react";
import { isReceivableTrip } from "./selectors";

interface ReadinessChipProps {
  trip: Trip;
  /** Pass when waypoints are loaded separately (e.g. the trips list). */
  waypointCount?: number;
}

/**
 * The handoff gate (dispatch redesign § recs-2), rendered as three segments —
 * Stops · Driver · Receivable. A met segment shows a green check; an unmet one
 * is greyed with its type icon. "Receivable" is met once the trip has both
 * stops and a driver (it can then be received/worked by the operator app).
 */
function Segment({
  met,
  icon,
  label,
}: {
  met: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-1 rounded-md px-1.5 py-[3px]"
      style={{
        background: met ? "#dcfce7" : "#f4f4f5",
        color: met ? "#15803d" : "#a1a1aa",
      }}
      title={label}
    >
      {met ? <Check className="h-[11px] w-[11px]" strokeWidth={2.6} /> : icon}
      <span className="text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </span>
    </div>
  );
}

export function ReadinessChip({ trip, waypointCount }: ReadinessChipProps) {
  const r = isReceivableTrip(trip, waypointCount);
  const ready = r.hasWaypoints && r.hasOperator;
  return (
    <div className="flex items-center gap-1">
      <Segment
        met={r.hasWaypoints}
        icon={<MapPin className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Stops"
      />
      <Segment
        met={r.hasOperator}
        icon={<User className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Driver"
      />
      <Segment
        met={ready}
        icon={<Zap className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Receivable"
      />
    </div>
  );
}
